import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export type PageHeaderIcon =
  | 'dashboard'
  | 'documents'
  | 'activity'
  | 'playground'
  | 'template'
  | 'key'
  | 'webhook'
  | 'analytics'
  | 'report'
  | 'billing'
  | 'settings'
  | 'support'
  | 'help'
  | 'examples'
  | 'business'
  | 'admin'
  | 'people'
  | 'default';

export interface PageHeaderMeta {
  title: string;
  icon: PageHeaderIcon;
  subtitle?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PageTitleService {
  private router = inject(Router);
  private headerSubject = new BehaviorSubject<PageHeaderMeta>({ title: 'Dashboard', icon: 'dashboard' });

  readonly title$: Observable<string> = this.headerSubject.pipe(map((h) => h.title));
  readonly header$: Observable<PageHeaderMeta> = this.headerSubject.asObservable();

  constructor() {
    const events = this.router?.events;
    if (events) {
      events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.resolveHeader(this.router.routerState.snapshot.root))
      ).subscribe((header) => this.headerSubject.next(header));
    }

    const root = this.router?.routerState?.snapshot?.root;
    if (root) {
      this.headerSubject.next(this.resolveHeader(root));
    }
  }

  private resolveHeader(route: ActivatedRouteSnapshot): PageHeaderMeta {
    const title = this.resolveTitle(route);
    const icon = this.resolveIcon(route, this.router.url);
    return { title, icon, subtitle: undefined };
  }

  /** Page intro line shown in the app shell (set by shell-aligned page headers). */
  setShellSubtitle(subtitle?: string): void {
    const current = this.headerSubject.value;
    this.headerSubject.next({
      ...current,
      subtitle: subtitle?.trim() || undefined
    });
  }

  private resolveTitle(route: ActivatedRouteSnapshot): string {
    let current: ActivatedRouteSnapshot | null = route;

    while (current?.firstChild) {
      current = current.firstChild;
    }

    while (current) {
      const dataTitle = current.data['title'] as string | undefined;
      if (dataTitle) {
        return this.normalizePageTitle(dataTitle);
      }
      if (typeof current.title === 'string' && current.title.trim()) {
        return this.normalizePageTitle(current.title);
      }
      current = current.parent;
    }

    return this.titleFromUrl(this.router.url);
  }

  private resolveIcon(route: ActivatedRouteSnapshot, url: string): PageHeaderIcon {
    let current: ActivatedRouteSnapshot | null = route;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    while (current) {
      const dataIcon = current.data['pageIcon'] as PageHeaderIcon | undefined;
      if (dataIcon) {
        return dataIcon;
      }
      current = current.parent;
    }
    return this.iconFromUrl(url);
  }

  private normalizePageTitle(title: string): string {
    return title
      .replace(/\s*[-–—]\s*ParseEasy\s*$/i, '')
      .replace(/^Super Admin\s+/i, '')
      .trim();
  }

  private titleFromUrl(url: string): string {
    const segments = url.split('?')[0].split('/').filter(Boolean);
    const segment = segments[segments.length - 1] || segments[0];
    if (!segment) {
      return 'Dashboard';
    }

    return segment
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private iconFromUrl(url: string): PageHeaderIcon {
    const path = url.split('?')[0].toLowerCase();
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/organizations')) return 'business';
    if (path.includes('/admin-users')) return 'admin';
    if (path.includes('/users')) return 'people';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/billing')) return 'billing';
    if (path.includes('/templates')) return 'template';
    if (path.includes('/ai-models') || path.includes('/config') || path.includes('/feature-flags')) return 'settings';
    if (path.includes('/monitoring')) return 'analytics';
    if (path.includes('/support-team') || path.includes('/support')) return 'support';
    if (path.includes('/audit') || path.includes('/reports')) return 'report';
    if (path.includes('/rbac') || path.includes('/ip-controls')) return 'admin';
    if (path.includes('/impersonate')) return 'people';
    if (path.includes('/documents')) return 'documents';
    if (path.includes('/parsing-engine')) return 'analytics';
    if (path.includes('/super-admin')) return 'dashboard';
    return 'default';
  }
}
